import * as R from 'ramda';
import React from 'react';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryLegend,
  VictoryLine,
  VictoryScatter,
  VictoryStack,
  VictoryTooltip
} from "victory";

import {translate} from 'iss/strings';
import {
  observeAllActivations,
  observeFinishedEvents,
  observeNewActivations,
  observePersonEvaluations,
  observeSecurityObservations,
  observeWorkQuotes
} from 'iss/service/StatisticsService';
import dates from 'iss/utils/dates';
import {SubscribeTo} from 'iss/utils/subscriptionUtils';
import {Flex, FlexItem} from 'iss/widgets/Flex';
import { DownloadBlobButton } from '../widgets/DownloadBlobButton';

const graphColors = [
  '#08395b',
  '#3f7eac',
  '#e10909',
  '#a4c8e1',
  '#ff893f',
  '#c7f464',
];
const defaultFontFamily = '"Arial", "Helvetica", "Verdana", sans-serif';

function ReportsPage() {
  return <React.Fragment>
    <h1>{translate('reports.title')}</h1>
    <Flex flexWrap="wrap">
      <ReportGraph title="reports.finishedEvents" monitor={observeFinishedEvents} />
      <ReportGraph title="reports.newActivations" monitor={observeNewActivations} />
      <ReportGraph title="reports.allActivations" monitor={observeAllActivations} />
      <ReportGraph title="reports.allActivationsByServiceCategory" monitor={observeAllActivations} graphComponent={ActivationBarChart}/>
      <ReportGraph title="reports.personEvaluations" monitor={observePersonEvaluations} />
      <ReportGraph title="reports.securityObservations" monitor={observeSecurityObservations} />
      <ReportGraph title="reports.workQuotes" monitor={observeWorkQuotes} graphComponent={WorkQuoteBarChart}/>
    </Flex>
  </React.Fragment>;
}

function ReportGraph({title, monitor, graphComponent}) {
  const GraphComponent = graphComponent || LineChart;
  return <FlexItem flex="0 0 50%" padding="10px 10px 30px 10px">
    <h1>{translate(title)}</h1>
    <SubscribeTo observable={monitor}>
      {(data) => (data && data.length) ?
        <GraphComponent data={data} /> : translate('reports.noData')}
    </SubscribeTo>
    </FlexItem>;
}

function ActivationBarChart({data}) {
  const serviceCategoryNames = getServiceCategoryNames(data);
  const legendColumns = 2;
  const legendRows = 3;

  const processedData = serviceCategoryNames.map((name) => {
    return data.map((dateObj) => {
      return {
        year: dateObj.year,
        month: dateObj.month,
        count: countServiceCategoriesByName(dateObj, name),
        serviceName: name
      };
    });
  });

  const flatProcessedData = processedData.flat();

  const transformedObject = flatProcessedData.reduce((acc, item) => {
    const { serviceName } = item;
    if (!acc[serviceName]) {
      acc[serviceName] = [];
    }
    acc[serviceName].push(item);
    return acc;
  }, {});

  return <>
      <DefaultChart>
        <DefaultLegend
          x={50}
          y={legendRows * -15}
          height={legendRows * 45}
          width={400}
          itemsPerRow={legendColumns}
          data={[
            ...serviceCategoryNames.map(serviceName =>
              ({name: serviceName})
            )
          ]} />
        <VictoryStack>
          { serviceCategoryNames.map((serviceName, index) =>
            <VictoryBar data={transformedObject[serviceName]}
              key={serviceName}
              {...defaultBarProps}
              labels={({ datum }) => `${datum.serviceName}: ${datum.count}`}
              labelComponent={<VictoryTooltip dy={40} />}
              y="count"
              style={{ data: {fill: graphColors[index]}, labels: {fill: "black" } }}
            />
          )}
        </VictoryStack>
      </DefaultChart>
    </>;
}

function ExportButton({url, text}) {
  // Use an empty (MIME) type so that the default 'application/json' isn't used - seems to work without it (at least with PDFs)
  return <DownloadBlobButton
    type=''
    buttonLook='primary'
    url={url}
    text={text}
    />;
}

function WorkQuoteBarChart({data}) {
  const types = getWorkQuoteTypes(data);
  const barCount = (types.length + 1);
  const legendColumns = 2;
  const legendRows = barCount > legendColumns ? 2 : 1;

  const processedData = types.map((type) => {
    return data.map((dateObj) => {
      return {
        year: dateObj.year,
        month: dateObj.month,
        count: countWorkQuotesByType(dateObj, type),
        type: type,
      };
    });
  });

  const flatProcessedData = processedData.flat();

  const transformedObject = flatProcessedData.reduce((acc, item) => {
    const { type } = item;
    if (!acc[type]) {
      acc[type] = [];
    }
    acc[type].push(item);
    return acc;
  }, {});

  return <>
    <ExportButton
      url={'/work-quote/export'}
      text={translate('reports.workQuoteDownload')} />
    <DefaultChart>
      <DefaultLegend
        x={50}
        y={legendRows * -15}
        height={legendRows * 45}
        width={400}
        itemsPerRow={legendColumns}
        data={[
          ...types.map(type =>
            ({name: translate('reports.workQuoteTypes.'+type)})
          )
        ]} />
      <VictoryStack>
        { types.map((type, index) =>
          <VictoryBar data={transformedObject[type]}
            key={type}
            {...defaultBarProps}
            labels={({datum}) => `${datum.count}`}
            labelComponent={<VictoryTooltip dy={40} />}
            y="count"
            style={{ data: {fill: graphColors[index]}, labels: {fill: "black" } }}
          />
        )}
      </VictoryStack>
    </DefaultChart>
    </>;
}

function getWorkQuoteTypes(data) {
  return R.pipe(
    R.chain(R.prop('byWorkQuoteType')), //flatMap
    R.map(R.prop('type')),
    R.uniq
  )(data);
}

function getServiceCategoryNames(data) {
  return R.pipe(
    R.chain(R.prop('byServiceType')), //flatMap
    R.map(R.prop('serviceTypeName')),
    R.uniq
  )(data);
}

function countWorkQuotesByType(item, type) {
  const monthObject = item.byWorkQuoteType.find((obj) => obj.type === type);
  if(monthObject){
    return monthObject.count;
  }
  return 0;
}

function countServiceCategoriesByName(item, name) {
  const monthObject = item.byServiceType.find((obj) => obj.serviceTypeName === name);
  if(monthObject){
    return monthObject.count;
  }
  return 0;
}

function LineChart({data}) {
  return <DefaultChart>
    <VictoryScatter data={data}
      {...defaultScatterProps}
      padding={{bottom: 60}}
      size={5}
      labels={({ datum }) => `${datum.count}\n${datum.month}/${datum.year}`}
      labelComponent={
        <VictoryTooltip
          dy={-20}
        />
      }
      y="count"
    />
    <VictoryLine data={data}
      {...defaultLineProps}
      y="count"
    />
  </DefaultChart>;
}

function DefaultChart({children}) {
  return <VictoryChart
    height={300}
    width={400}
    padding={{top: 50, left: 60, bottom: 60, right: 30}}
    minDomain={{ y: 0 }}
    domainPadding={{x: 30}}>
    {children}
    <VictoryAxis
      dependentAxis
      style={{
        tickLabels: {fontFamily: defaultFontFamily},
      }}
   />
    <VictoryAxis
      style={{
        tickLabels: {angle: 45, verticalAnchor: 'middle', textAnchor: 'start', fontFamily: defaultFontFamily},
      }}
    />
  </VictoryChart>;
}

function DefaultLegend(props) {
  return <VictoryLegend
    {...props}
    orientation="horizontal"
    gutter={5}
    rowGutter={-5}
    colorScale={graphColors}
    standalone={false}
    style={{
      labels: {
        fontFamily: defaultFontFamily
      },
    }}
  />;
}

const defaultBarProps = {
  sortKey:["year", "month"],
  style: { data: {fill: graphColors[0]} },
  x: formatMonthYear,
  barWidth:20
};

const defaultLineProps = {
  sortKey:["year", "month"],
  style: { data: {stroke: graphColors[0]} },
  x: formatMonthYear
};

const defaultScatterProps = {
  sortKey:["year", "month"],
  x: formatMonthYear
};

function formatMonthYear({month, year}) {
  return dates.formatMonth(year+"-"+(month < 10 ? "0" : "") + month+"-01");
}

export default ReportsPage;



// WEBPACK FOOTER //
// ./modules/issFrontend/pages/ReportsPage.jsx